import { AxiosResponse } from "axios";
import { normalize, schema } from 'normalizr';
import { createAsyncThunk } from "@reduxjs/toolkit";
import robloxApi from '../../api/robloxApi';
import robloxUrls from "../../configs/roblox/robloxUrls";
import { SkinInterface } from "../../store/roblox/skinsSlice";
import { userEntity, usersEntity } from "./usersActions";
import { UserInterface } from "../../store/roblox/usersSlice";

const PREFIX = 'roblox';

const processStrategy = (value: any) => {
  return {
    ...value,
    url: robloxUrls.skinPath(value.id),
    mask_url: robloxUrls.remoteUrl(value.mask_url),
    texture_url: robloxUrls.remoteUrl(value.texture_url),
    thumbnail_url: robloxUrls.remoteUrl(value.thumbnail_url)
  };
}

export const skinsEntity = new schema.Entity(
  'skins',
  {
    user: usersEntity
  },
  {
    processStrategy
  }
);

export const skinEntity = new schema.Entity(
  'skin',
  {
    user: userEntity
  },
  {
    processStrategy
  }
);

const normalizeInstance = (response: AxiosResponse) => {
  const normalized = normalize<
    any,
    {
      user: { [key: string]: UserInterface };
      skin: SkinInterface
    }
    >(response.data.skin, skinEntity);
  return normalized.entities;
}

const normalizeCollection = (response: AxiosResponse) => {
  const normalized = normalize<
    any,
    {
      users: { [key: string]: UserInterface };
      skins: { [key: string]: SkinInterface };
    }
    >(response.data.skins, [skinsEntity]);

  return {
    users: normalized.entities.users || {},
    skins: normalized.entities?.skins || {},
    ids: normalized.result,
    count: response.data.meta?.total_count
  };
}

export const fetchSkins = createAsyncThunk(
  `${PREFIX}_skins/fetchSkins`,
  async ({ params, target, collection }: { params: object; target: string; collection?: string }) => {
    let url = `/onesky/${target}`;
    url = collection ? `${url}/${collection}` : url;

    const response = await robloxApi.get(url, { params });
    return normalizeCollection(response);
  }
);

export const fetchUserSkins = createAsyncThunk(
  `${PREFIX}_skins/fetchSkins`,
  async ({ userId, params = {} }: { userId: number | string; params?: object; }) => {
    const response = await robloxApi.get(`onesky/users/${userId}/skins`, { params });
    return normalizeCollection(response);
  }
);

export const fetchSkin = createAsyncThunk(
  `${PREFIX}_skins/fetchSkin`,
  async (id: number | string) => {
    const response = await robloxApi.get(`/onesky/skins/${id}`);
    return normalizeInstance(response);
  }
);

export const createSkin = createAsyncThunk(
  `${PREFIX}_skins/createSkin`,
  async (data: object, { rejectWithValue }) => {
    try {
      const response = await robloxApi.post(`/onesky/skins`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error);
    }
  }
);

export const updateSkin = createAsyncThunk(
  `${PREFIX}_skins/updateSkin`,
  async ({ id, data }: { id: number, data: object }, { rejectWithValue }) => {
    try {
      const response = await robloxApi.patch(`/onesky/skins/${id}`, data);
      return normalizeInstance(response);
    } catch (error: any) {
      return rejectWithValue(error.response.data || error)
    }
  }
);

export const destroySkin = createAsyncThunk(
  `${PREFIX}_skins/destroySkin`,
  async (id: number) => {
    const response = await robloxApi.delete(`/onesky/skins/${id}`);
    return response.data;
  }
);
